/**
 * DEV-1044 — redeeming a packhouse's grower invite.
 *
 * The link a grower receives is `https://<producer-app-host>/invite?token=…`,
 * where the host is whichever tenant app serves their country (theboer.co.za
 * for ZA; another domain elsewhere — the backend resolves it, see
 * `Services/Portal/portal-invite-landing.ts`). The token is single-use,
 * email-bound and expires in seven days.
 *
 * Three things make this awkward, and all three are why the token is parked in
 * sessionStorage rather than simply read where it is used:
 *
 *  1. **The redeem needs an authenticated caller**, and the visitor arriving on
 *     this link usually has no account at all. So the token has to survive a
 *     full round trip out to Auth0 and back to `/auth0/callback` — a different
 *     path, with the original query string gone.
 *  2. **The token must not linger in the address bar.** It is
 *     security-equivalent to a password: it would be copied into a support
 *     ticket, shared in a screenshot, or left in browser history. It is
 *     stripped from the URL the moment it is read.
 *  3. **It is consumed exactly once.** `take()` clears before returning, so a
 *     retry after a failed redeem cannot burn a second attempt on a token the
 *     server already marked REDEEMED.
 *
 * sessionStorage, not localStorage: one tab, one invite, gone when the tab is.
 */
import { redeemInvite } from "./session";

const PENDING_INVITE_KEY = "metaship:pendingInviteToken";
export const INVITE_PATH = "/invite";

/**
 * Read `?token=` on the invite path, park it, and scrub the URL.
 * Safe to call on every boot — a no-op anywhere else.
 */
export function captureInviteToken(pathname: string, search: string): void {
  if (pathname !== INVITE_PATH) return;
  let token: string | null = null;
  try {
    token = new URLSearchParams(search).get("token");
  } catch {
    return;
  }
  if (!token || !token.trim()) return;

  try {
    sessionStorage.setItem(PENDING_INVITE_KEY, token.trim());
  } catch {
    // Private mode / storage disabled. The redeem below simply will not run;
    // the visitor still reaches the normal sign-in, which is a worse outcome
    // than being let straight in but not a broken one.
  }
  // Land on "/" with no token in the bar. replaceState, not assign: a reload
  // must not re-trigger the Auth0 round trip from a URL that no longer has it.
  try {
    window.history.replaceState({}, "", "/");
  } catch {
    /* non-fatal */
  }
}

export function hasPendingInvite(): boolean {
  try {
    return !!sessionStorage.getItem(PENDING_INVITE_KEY);
  } catch {
    return false;
  }
}

/** Read and clear in one step — a token is never offered twice. */
function takeInviteToken(): string | null {
  try {
    const token = sessionStorage.getItem(PENDING_INVITE_KEY);
    sessionStorage.removeItem(PENDING_INVITE_KEY);
    return token;
  } catch {
    return null;
  }
}

export interface RedeemedInvite {
  organisationId: number;
  organisationName: string;
}

/**
 * Redeem a parked invite, if there is one. Returns null when there was nothing
 * to redeem OR when the redeem failed.
 *
 * **Never throws.** A failed redeem must not strand the visitor on an error
 * page: they are still a legitimately signed-in user, and the normal access
 * gate (organisation picker, or "no access") is a far better place to land than
 * a stack trace. The common failures are all benign-looking to the user —
 * the link expired, it was already used, or it was addressed to a different
 * mailbox than the one they just signed in with.
 */
export async function redeemPendingInvite(bearer: string): Promise<RedeemedInvite | null> {
  const token = takeInviteToken();
  if (!token) return null;
  try {
    return await redeemInvite(token, bearer);
  } catch {
    return null;
  }
}
